import { faDollarSign, faShieldHalved } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React from 'react';

import Debug from '@/components/Debug';
import { Color } from '@/constants/color';
import { PAYMENT_TYPE } from '@/constants/treatability';
import { titleize } from '@/pages/BillingReportResults/utils/titleize';
import { TreatabilityMatch } from '@/types/treatabilityMatch';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/ui/Accordion';
import Badge from '@/ui/components/Badge';
import { Pill } from '@/ui/Pill';
import { Tooltip } from '@/ui/Tooltip';

import type { Props } from './types';

type ServicesByProgram = Record<string, TreatabilityMatch[]>;
type ServicesByConcern = Record<string, ServicesByProgram>;

const SECTION_LABELS: Record<PAYMENT_TYPE, string> = {
  [PAYMENT_TYPE.INSURANCE]: 'Insurance Covered',
  [PAYMENT_TYPE.SELF_PAY]: 'Self-Pay Only'
};

/** Tints the section header so the two payment types separate without reading a label */
const SECTION_COLORS: Record<PAYMENT_TYPE, 'primary' | 'warning'> = {
  [PAYMENT_TYPE.INSURANCE]: 'primary',
  [PAYMENT_TYPE.SELF_PAY]: 'warning'
};

const SECTION_TOOLTIPS: Record<PAYMENT_TYPE, string> = {
  [PAYMENT_TYPE.INSURANCE]: "Billable to the member's insurance plan.",
  [PAYMENT_TYPE.SELF_PAY]: 'Not billable to insurance — the member pays out of pocket.'
};

const groupByConcernAndProgram = (services: TreatabilityMatch[]): ServicesByConcern =>
  services.reduce<ServicesByConcern>((acc, s) => {
    acc[s.primary_concern] ??= {};
    (acc[s.primary_concern][s.program_name] ??= []).push(s);
    return acc;
  }, {});

const uniquePhaseSummary = (programsByName: ServicesByProgram): string => {
  const seen = new Set<string>();
  const phases: TreatabilityMatch[] = [];
  Object.values(programsByName)
    .flat()
    .sort((a, b) => a.phase_order - b.phase_order)
    .forEach((s) => {
      if (!seen.has(s.phase_display_name)) {
        seen.add(s.phase_display_name);
        phases.push(s);
      }
    });
  return phases.map((s) => s.phase_display_name).join(' · ');
};

const PriorAuthBadge = ({ value }: { value: TreatabilityMatch['prior_auth_required'] }) => {
  if (value === 'yes') {
    return (
      <Badge color={Color.AMBER}>
        <span className="flex items-center gap-1">
          <FontAwesomeIcon icon={faShieldHalved} />
          Prior Auth Required
        </span>
      </Badge>
    );
  }
  if (value === 'no') {
    return <Badge color={Color.GREEN}>No Prior Auth</Badge>;
  }
  return <Badge color={Color.GRAY}>Prior Auth Unknown</Badge>;
};

const PaymentTypeSection = ({ paymentType, services, defaultOpen }: Props) => {
  const isSelfPay = paymentType === PAYMENT_TYPE.SELF_PAY;
  const servicesByConcern = groupByConcernAndProgram(services);
  const concernNames = Object.keys(servicesByConcern).sort();

  if (concernNames.length === 0) {
    return null;
  }

  return (
    <AccordionItem variant="subtle" color={SECTION_COLORS[paymentType]} defaultOpen={defaultOpen}>
      <AccordionTrigger
        aria-label={SECTION_LABELS[paymentType]}
        /* The warning scheme's yellow-100 header washes out the filled pill sitting
           on it, so drop to the same weight of tint the insurance section carries. */
        className={isSelfPay ? 'bg-yellow-50 hover:bg-yellow-100' : undefined}>
        <span className="flex items-center gap-2">
          <Tooltip content={SECTION_TOOLTIPS[paymentType]}>
            <Pill
              variant="filled"
              color={SECTION_COLORS[paymentType]}
              size="sm"
              icon={isSelfPay ? faDollarSign : undefined}>
              {SECTION_LABELS[paymentType]}
            </Pill>
          </Tooltip>
          <span className="text-xs font-normal">
            {services.length} {services.length === 1 ? 'program' : 'programs'}
          </span>
        </span>
      </AccordionTrigger>
      <AccordionContent className="text-uniform-gray-700">
        <Accordion>
          {concernNames.map((concern) => {
            const programsByName = servicesByConcern[concern];
            const programNames = Object.keys(programsByName).sort((a, b) =>
              (programsByName[a][0]?.program_family_name ?? a).localeCompare(
                programsByName[b][0]?.program_family_name ?? b
              )
            );
            const phaseSummary = uniquePhaseSummary(programsByName);
            return (
              <AccordionItem key={concern}>
                <AccordionTrigger>
                  <span className="flex flex-col">
                    <span className="font-bold">{titleize(concern) || 'Unknown Concern'}</span>
                    <span className="text-xs text-gray-500 font-normal">{phaseSummary}</span>
                  </span>
                </AccordionTrigger>
                <AccordionContent>
                  <div className="flex flex-col gap-4">
                    {programNames.map((programName) => (
                      <div key={programName}>
                        <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-1">
                          {titleize(programName)}
                        </p>
                        <div className="flex flex-col gap-1">
                          {programsByName[programName].map((service: TreatabilityMatch) => (
                            <div
                              key={service.id}
                              className="relative flex items-center justify-between py-1.5 px-2 rounded-md hover:bg-gray-50">
                              <span className="text-sm font-medium">
                                {service.phase_display_name}
                              </span>
                              <div className="flex items-center gap-2">
                                <PriorAuthBadge value={service.prior_auth_required} />
                                <Debug object={service} buttonClassName="relative" />
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </AccordionContent>
              </AccordionItem>
            );
          })}
        </Accordion>
      </AccordionContent>
    </AccordionItem>
  );
};

export default React.memo(PaymentTypeSection);
